import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight, CheckCircle2 } from "lucide-react";

const LEVELS = [
  { code: "Bac+3", name: "Bachelor", desc: "Cycle initial, fondamentaux du business et premier stage long.", years: "3 ans" },
  { code: "Bac+5", name: "Master / MBA", desc: "Spécialisation et leadership stratégique, mémoire ou création d'entreprise.", years: "2 ans" },
  { code: "Bac+5+", name: "MBA Executive", desc: "Pour les cadres et dirigeants en activité. Format week-ends sur 11 mois, sélection sur dossier et expérience.", years: "11 mois" },
];

const ENTRIES = [
  "Post-bac (Terminale validée)",
  "Admission parallèle Bac+2 / Bac+3",
  "Admission parallèle Bac+4 / Bac+5",
  "Cadres en activité (Executive)",
];

export const WhyChoose = () => {
  const navigate = useNavigate();
  return (
    <section id="why-us" data-testid="why-us-section" className="section-padding bg-white !pt-8 md:!pt-12">
      <div className="container-edge">

        <div className="max-w-3xl mb-16">
          <div className="overline mb-4">Pourquoi nous choisir</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
            Apprendre en agissant. Progresser en prouvant. <em className="text-navy-500">Voilà notre manière de faire</em>
          </h2>
          <p className="text-[#555] mt-6 text-[16px] leading-relaxed">
            Oubliez les classes surchargées et les théories sans application. Ici,
            chaque cours est une opportunité de prouver, de construire, de progresser.
          </p>
          <div className="mt-8">
            <button onClick={() => navigate("/contact#candidater")} className="btn-red">
              Candidature
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">

          {/* Left: stat + title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="overline mb-4 flex items-center gap-3">
              <GraduationCap className="h-4 w-4" strokeWidth={1.6} />
              Niveaux & Admission
            </div>
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl text-navy-700 leading-tight mb-6">
              Du <em className="not-italic red-shine">post-bac</em> au bac+5.
            </h3>
            <p className="text-[#555] text-[16px] leading-relaxed mb-8">
              Admission sur entretien et concours. Accessible post-bac et en
              admission parallèle de Bac+2 à Bac+5. Un parcours continu, ou
              une porte d'entrée à la hauteur de votre niveau.
            </p>

            <div className="overline mb-3">Voies d'admission</div>
            <ul className="space-y-2.5 mb-8">
              {ENTRIES.map((e) => (
                <li key={e} className="flex gap-3 text-[14px] text-[#333]">
                  <CheckCircle2 className="h-4 w-4 text-navy-500 shrink-0 mt-0.5" />
                  {e}
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate("/admissions")}
              className="btn-outline-navy"
            >
              Process d'admission
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>

          {/* Right: timeline of levels */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="col-span-12 lg:col-span-7 relative"
          >
            <div className="absolute left-[1.25rem] top-2 bottom-2 w-px bg-navy-100" aria-hidden="true" />
            <div className="space-y-6">
              {LEVELS.map((lvl, i) => (
                <motion.div
                  key={lvl.code}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative pl-14"
                >
                  <span className="absolute left-0 top-1 w-10 h-10 bg-navy-700 text-white text-[10px] font-mono uppercase tracking-wider flex items-center justify-center">
                    {lvl.code}
                  </span>
                  <div className="bg-[#FAF8F3] p-6 border border-navy-100">
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <h3 className="font-display text-xl md:text-2xl text-navy-700">{lvl.name}</h3>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-navy-500">{lvl.years}</span>
                    </div>
                    <p className="text-[#555] text-[14px] leading-relaxed">{lvl.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default WhyChoose;
